import { RefObject } from "react";
import createImageIdsAndCacheMetaData from "./createImageIdsAndCacheMetaData";
import createNiftiImageIds from "./createNiftiImageIds";
import readNiftiVoxels from "./helpers/readNiftiVoxels";
import initCornerstone from "./initCornerstone";
import {
  RenderingEngine,
  volumeLoader,
  setVolumesForViewports,
  cache,
  getRenderingEngine,
  imageLoadPoolManager,
  ImageVolume,
} from "@cornerstonejs/core";
import {
  StackScrollTool,
  ToolGroupManager,
  CrosshairsTool,
  Enums as csToolsEnums,
  segmentation as cstSegmentation,
  WindowLevelTool,
  PanTool,
  ZoomTool,
  BrushTool,
  utilities,
} from "@cornerstonejs/tools";
import {
  SegmentationIds,
  RenderingEngineIds,
  ViewerIds,
} from "../types/cornerstone";
import {
  PublicViewportInput,
  IRenderingEngine,
} from "@cornerstonejs/core/dist/esm/types";
import * as cornerstoneTools from "@cornerstonejs/tools";
import getIds from "./helpers/getIds";
import { brushInstanceNames, brushStrategies } from "./helpers/toolsName";
import {
  getSegInfo,
  injectBufferIntoSegVolume,
  SegInfo,
} from "./generateSegmentation";
import viewerState, { isNiftiMetadata, type Segment } from "stores/viewer";
import dcmjs from "dcmjs";
import roiSummary from "./commons/roiSummary.json";

const { MouseBindings, KeyboardBindings } = cornerstoneTools.Enums;

export async function initViewer(
  id: string,
  viewerRefs: RefObject<HTMLDivElement>[],
  instanceMetaData: dcmjs.data.NaturalizedDataset,
  notify: (message: string, options?: object) => void,
) {
  if (!viewerState.instanceMetadata) return;
  viewerState.viewerId = id;

  const rawMeta = viewerState.instanceMetadata;
  const isNifti = isNiftiMetadata(rawMeta);
  const niftiUrl = isNifti ? rawMeta._niftiUrl : undefined;
  const niftiSegUrl = isNifti ? rawMeta._niftiSegUrl : undefined;
  const niftiSegUrls = isNifti ? rawMeta._niftiSegUrls : undefined;

  const {
    renderingEngineId,
    viewportInputConf,
    volumeId,
    segVolumeId,
    segmentationId,
    toolGroupId,
    viewportId1,
    viewportId2,
    viewportId3,
  } = getIds();

  const renderingEngine = await setupRenderingEngine(viewerRefs, id, {
    renderingEngineId,
    viewportInputConf,
  });

  // ═══ NIfTI branch — return early, never reach DICOM code ═══
  if (isNifti && niftiUrl) {
    const niftiVolumeId = `nifti:${niftiUrl}`;

    // 1. Load and display the base volume ASAP
    await loadNiftiVolume(
      {
        volumeId: niftiVolumeId,
        viewportId1,
        viewportId2,
        viewportId3,
      },
      niftiUrl,
      renderingEngine,
    );

    // Activate tools immediately after volume is visible
    const toolGroup = ToolGroupManager.getToolGroup(toolGroupId);
    if (toolGroup) {
      toolGroup.setToolActive(CrosshairsTool.toolName, {
        bindings: [{ mouseButton: MouseBindings.Primary }],
      });
    }

    // Optional: force an early render if Cornerstone doesn't already do it
    renderingEngine.render();

    // 2. Build quantification metadata immediately
    if (niftiSegUrls) {
      const { segments, datasets } = buildNiftiSegmentsFromRoiSummary(
        niftiSegUrls,
        segmentationId,
      );

      viewerState.segmentsPerSeries = segments;
      viewerState.niftiDatasets = datasets;
    }

    // 3. Load segmentations as DERIVED labelmaps (see loadNiftiSegmentation).
    //    Serial: each derive depends on its modality's base volume being in
    //    cache; serial also avoids the parallel-load texture races we hit.
    const segmentationPromise = (async () => {
      const bases = (viewerState.niftiBases ?? {}) as Record<string, string>;

      if (niftiSegUrls) {
        let idx = 0;
        for (const [setType, segUrl] of Object.entries(niftiSegUrls)) {
          // Each seg derives from the base volume of ITS OWN modality so the
          // labelmap shares that base's geometry + imageId system.
          const modality = BV_TYPE_TO_MODALITY[setType] ?? "t1mri";
          const baseUrl = bases[modality] ?? niftiUrl;
          await loadNiftiSegmentation(segUrl, {
            segmentationId: `${segmentationId}_nifti_${idx}`,
            baseVolumeId: `nifti:${baseUrl}`,
          });
          idx++;
        }
      } else if (niftiSegUrl) {
        await loadNiftiSegmentation(niftiSegUrl, {
          segmentationId: `${segmentationId}_nifti_0`,
          baseVolumeId: niftiVolumeId,
        });
      }
    })();

    // Option A: don't wait for segmentations
    segmentationPromise.catch((err) => {
      console.error("Failed to load NIfTI segmentations", err);
    });

    return renderingEngine;
  }
  // ═══ END NIfTI branch ═══

  // ── Original DICOM path (unchanged) ──

  const [imageIds, getSEGPixelData] = await createImageIdsAndCacheMetaData({
    StudyInstanceUID: instanceMetaData.StudyInstanceUID!,
    SeriesInstanceUID: instanceMetaData.SeriesInstanceUID!,
  });
  const segInfo = getSEGPixelData?.().then((SEGArrayBuffer) =>
    //@ts-ignore
    getSegInfo(imageIds, SEGArrayBuffer),
  );

  const volume = await createAndLoadVolume(
    { volumeId, viewportId1, viewportId2, viewportId3 },
    imageIds,
    renderingEngine,
  );
  const toolGroup = ToolGroupManager.getToolGroup(toolGroupId);
  if (toolGroup) {
    toolGroup.setToolActive(CrosshairsTool.toolName, {
      bindings: [{ mouseButton: MouseBindings.Primary }],
    });
  }

  loadFramesMiddleOutwards(
    volume,
    { volumeId, viewportId1, viewportId2, viewportId3 },
    renderingEngine,
  );

  await setupSegmentation(
    {
      volumeId: volume.volumeId,
      segVolumeId,
      segmentationId,
      toolGroupId,
    },
    segInfo,
    notify,
  );
  return renderingEngine;
}

// ═══════════════════════════════════════════════════════════════
//  NIfTI helpers
// ═══════════════════════════════════════════════════════════════

async function loadNiftiVolume(
  viewerIds: ViewerIds,
  niftiUrl: string,
  renderingEngine: IRenderingEngine,
) {
  const { volumeId, viewportId1, viewportId2, viewportId3 } = viewerIds;
  const viewportIds = [viewportId1, viewportId2, viewportId3];

  let volume = cache.getVolume(volumeId) as ImageVolume | undefined;
  if (!volume) {
    const imageIds = await createNiftiImageIds(niftiUrl);
    volume = await volumeLoader.createAndCacheVolume(volumeId, { imageIds });
  }
  await volume.load();

  try {
    const [dimX, dimY, dimZ] = volume.dimensions;
    const [spX, spY, spZ] = volume.spacing;

    // fix X and Y Spacing
    if (dimX === 196 && Math.abs(spY - 1.2) < 0.01) {
      const correctSpacing: [number, number, number] = [spY, spX, spZ]; // [1.2000, 1.0156, 1.0156]
      volume.spacing = correctSpacing;

      const vtkImageData = (volume as any).imageData;
      if (vtkImageData?.setSpacing) {
        vtkImageData.setSpacing(correctSpacing);
        vtkImageData.modified();
      }
    }
  } catch (e) {
    console.warn("Failed to correct volume spacing:", e);
  }
  // ════════════════════════════════════════════════════════════════════════

  await setVolumesForViewports(renderingEngine, [{ volumeId }], viewportIds);

  jumpViewportsToCenterOfVolume(renderingEngine, viewportIds, volume);

  for (const vpId of viewportIds) {
    const vp = renderingEngine.getViewport(vpId) as any;
    vp.resetCamera();
    vp.render();
  }
  return volume;
}

function jumpViewportsToCenterOfVolume(
  renderingEngine: IRenderingEngine,
  viewportIds: string[],
  volume: ImageVolume,
) {
  try {
    const { dimensions, imageData } = volume as any;
    if (!dimensions || !imageData?.indexToWorld) return;

    const centerIndex: [number, number, number] = [
      Math.floor(dimensions[0] / 2),
      Math.floor(dimensions[1] / 2),
      Math.floor(dimensions[2] / 2),
    ];

    const centerWorld = imageData.indexToWorld(centerIndex) as [
      number,
      number,
      number,
    ];

    for (const vpId of viewportIds) {
      try {
        const viewport = renderingEngine.getViewport(vpId) as any;
        // setCamera with focalPoint repositions both the displayed slice and
        // the CrosshairsTool reference point for VolumeViewports.
        viewport?.setCamera?.({ focalPoint: centerWorld });
      } catch {
        // ignore per-viewport errors
      }
    }
  } catch {
    // ignore if volume metadata is not yet available
  }
}

/**
 * lightweight switch for displaySegment mode，keep segmentation and segmentsPerSeries unchanged.
 * provide displaySegment mode switching, without rebuilding segmentation and segmentsPerSeries.
 */
export async function switchNiftiBase(newBaseUrl: string) {
  const { renderingEngineId, viewportId1, viewportId2, viewportId3 } = getIds(
    viewerState.viewerId,
  );

  const renderingEngine = getRenderingEngine(renderingEngineId);
  if (!renderingEngine) return;

  const niftiVolumeId = `nifti:${newBaseUrl}`;
  const viewportIds = [viewportId1, viewportId2, viewportId3];

  const imageIds = await createNiftiImageIds(newBaseUrl);
  let volume = cache.getVolume(niftiVolumeId) as ImageVolume | undefined;
  if (!volume) {
    volume = await volumeLoader.createAndCacheVolume(niftiVolumeId, {
      imageIds,
    });
  }
  await volume.load();

  await setVolumesForViewports(
    renderingEngine,
    [{ volumeId: niftiVolumeId }],
    viewportIds,
  );

  // Re-center crosshairs on the new base volume.
  jumpViewportsToCenterOfVolume(renderingEngine, viewportIds, volume);

  renderingEngine.render();
}

/**
 * Load a NIfTI segmentation as a DERIVED labelmap (mirrors the DICOM path).
 *
 * Why derived (not an independent volume): a labelmap only renders reliably in
 * all three MPR viewports when it shares the base volume's geometry AND imageId
 * / actor system. The old approach created the seg as its own volume
 * (`createAndCacheVolume`) — geometry matched numerically, but the actor
 * lifecycle was independent, so only whichever viewport drew first showed it
 * ("random single viewport"). The DICOM path never had this because it derives
 * from the base with `createAndCacheDerivedVolume` + `injectBufferIntoSegVolume`.
 * This function does the NIfTI equivalent.
 *
 * Steps:
 *   1. Read the seg NIfTI's voxels DIRECTLY (readNiftiVoxels) — do NOT route it
 *      through createNiftiImageIds/createAndCacheVolume: that mints a fresh
 *      blob: URL each call whose imageIds fail to resolve ("Image not found for
 *      imageId: nifti:blob:..."), leaving the volume's scalar array EMPTY.
 *   2. Ensure the modality's base volume is in cache (derive source).
 *   3. Create a derived labelmap volume from that base (shares geometry).
 *   4. Write the labels into the derived volume's voxelManager.
 *   5. Register the segmentation pointing at the DERIVED volume.
 */
async function loadNiftiSegmentation(
  niftiSegUrl: string,
  {
    segmentationId,
    baseVolumeId,
  }: { segmentationId: string; baseVolumeId: string },
) {
  const segId = segmentationId;
  const derivedId = `${segId}__labelmap`;

  // Already registered → nothing to do.
  if (cstSegmentation.state.getSegmentation(segId)) return;

  // 1. Read label voxels straight from the .nii.gz (no cornerstone loader).
  const segVoxels = await readNiftiVoxels(niftiSegUrl);
  const srcScalars = segVoxels.data;

  // 2. Ensure the modality base volume is loaded (we derive from it). It may not
  //    be the active base (e.g. FLAIR seg while viewing T1), so load into cache
  //    if missing — without putting it on any viewport.
  let baseVolume = cache.getVolume(baseVolumeId) as ImageVolume | undefined;
  if (!baseVolume) {
    const baseUrl = baseVolumeId.replace(/^nifti:/, "");
    const baseImageIds = await createNiftiImageIds(baseUrl);
    baseVolume = await volumeLoader.createAndCacheVolume(baseVolumeId, {
      imageIds: baseImageIds,
    });
  }
  await baseVolume.load();

  // 3. Create a derived labelmap volume from the base (shares its geometry).
  let derived = cache.getVolume(derivedId) as ImageVolume | undefined;
  if (!derived) {
    derived = await volumeLoader.createAndCacheDerivedLabelmapVolume(
      baseVolumeId,
      { volumeId: derivedId },
    );
    // let the derived volume settle before writing (mirrors DICOM path)
    await new Promise((resolve) => setTimeout(resolve, 0));
    derived = cache.getVolume(derivedId) as ImageVolume;
  }
  if (!derived) {
    console.error(`[nifti seg] failed to create derived labelmap ${derivedId}`);
    return;
  }

  // 4. Geometry guard + write. Derived inherits the base's dims, so the source
  //    seg must have the same voxel count (each seg derives from its OWN
  //    modality's base, so T1 seg↔T1 base and FLAIR seg↔FLAIR base both match).
  const dims = derived.dimensions;
  const derivedVoxels = dims[0] * dims[1] * dims[2];
  if (srcScalars.length !== derivedVoxels) {
    console.warn(
      `[nifti seg] geometry mismatch, skipping ${segId}: ` +
        `src=${srcScalars.length} (${segVoxels.dimensions}) ` +
        `derived=${derivedVoxels} (${dims}) base=${baseVolumeId}`,
    );
    return;
  }
  if (!writeLabelsIntoVolume(derived, srcScalars)) {
    console.error(`[nifti seg] failed to write labels into ${derivedId}`);
    return;
  }

  // 5. Register the segmentation pointing at the DERIVED volume.
  cstSegmentation.addSegmentations([
    {
      segmentationId: segId,
      representation: {
        type: csToolsEnums.SegmentationRepresentations.Labelmap,
        data: { volumeId: derivedId },
      },
    },
  ]);
}

/**
 * Write label values into a derived labelmap volume's voxel storage.
 * Tries the v4 voxelManager APIs, then falls back to VTK scalars.
 * Returns true if the write landed.
 */
function writeLabelsIntoVolume(
  volume: ImageVolume,
  labels: ArrayLike<number>,
): boolean {
  const vm = (volume as any).voxelManager;

  // Preferred: write the whole array at once.
  if (vm?.setCompleteScalarDataArray) {
    try {
      vm.setCompleteScalarDataArray(Uint8Array.from(labels as any));
      vm.modified?.();
      return true;
    } catch (e) {
      console.warn("[nifti seg] setCompleteScalarDataArray failed:", e);
    }
  }

  // Fallback: get the writable array and copy in place.
  if (vm) {
    try {
      const scalarData = vm.scalarData ?? vm.getCompleteScalarDataArray?.();
      if (scalarData && scalarData.length === labels.length) {
        for (let i = 0; i < labels.length; i++) scalarData[i] = labels[i];
        vm.modified?.();
        return true;
      }
    } catch (e) {
      console.warn("[nifti seg] voxelManager direct write failed:", e);
    }
  }

  // Last resort: VTK point data.
  try {
    const scalars = (volume as any).imageData?.getPointData?.()?.getScalars?.();
    const arr = scalars?.getData?.();
    if (arr && arr.length === labels.length) {
      for (let i = 0; i < labels.length; i++) arr[i] = labels[i];
      (volume as any).imageData.modified();
      return true;
    }
  } catch (e) {
    console.warn("[nifti seg] imageData write failed:", e);
  }

  return false;
}

// ═══════════════════════════════════════════════════════════════
//  NIfTI QuantificationTable helpers
//
// build segmentsPerSeries for QuantificationTable from roiSummary.json
// grouped by modality (not bv_type) to preserve hierarchy tree completeness
// ═══════════════════════════════════════════════════════════════

const MODALITY_DISPLAY_NAMES: Record<string, string> = {
  t1mri: "T1 segmentation",
  t2flairmri: "WMH segmentation",
};

// bv_type → modality, derived once from roiSummary. Used to pick which base
// volume each segmentation should derive from (a seg must share its own
// modality's base geometry, not the active modality's).
const BV_TYPE_TO_MODALITY: Record<string, string> = (() => {
  const map: Record<string, string> = {};
  const walk = (node: any) => {
    if (node?.bv_type) map[node.bv_type] = node.modality ?? "t1mri";
    for (const c of node?.sub_rois ?? []) walk(c);
    for (const c of node?.lateral_rois ?? []) walk(c);
  };
  for (const group of Object.values(roiSummary)) walk(group);
  return map;
})();

const MODALITY_ORDER: Record<string, number> = {
  t1mri: 0,
  t2flairmri: 1,
};

function buildNiftiSegmentsFromRoiSummary(
  segUrls: Record<string, string>, // setType → url
  baseSegId: string, // e.g. "seg1"
): { segments: Segment[]; datasets: any[] } {
  const segments: Segment[] = [];

  // 1. Build setType → cornerstoneSegmentationId map
  const setTypeToSegId = new Map<string, string>();
  let idx = 0;
  for (const setType of Object.keys(segUrls)) {
    setTypeToSegId.set(setType, `${baseSegId}_nifti_${idx}`);
    idx++;
  }

  // 2. Collect all modalities that have at least one segment
  const modalitiesWithSegments = new Set<string>();

  for (const [groupName, group] of Object.entries(roiSummary)) {
    const g = group as any;
    if (!g.implemented || !g.bv_type) continue;

    const csSegmentationId = setTypeToSegId.get(g.bv_type);
    if (!csSegmentationId) continue;

    const modality: string = g.modality ?? "t1mri";
    const syntheticSeriesUID = `nifti:seg:${modality}`;

    modalitiesWithSegments.add(modality);

    // map and collect segments for this group and all its subgroups, using the same csSegmentationId
    collectRoiSegments(
      g,
      syntheticSeriesUID,
      csSegmentationId,
      segments,
      g.color,
      setTypeToSegId,
    );
  }

  // 3. Create ONE dataset per modality (not per bv_type)
  const datasets: any[] = [];
  for (const modality of modalitiesWithSegments) {
    datasets.push({
      SeriesInstanceUID: `nifti:seg:${modality}`,
      Modality: "SEG",
      SeriesDescription:
        MODALITY_DISPLAY_NAMES[modality] ?? `${modality} segmentation`,
      _order: MODALITY_ORDER[modality] ?? 99,
    });
  }

  return { segments, datasets };
}

function collectRoiSegments(
  roi: any,
  seriesUID: string,
  segmentationId: string,
  segments: Segment[],
  parentColor?: number[],
  setTypeToSegId?: Map<string, string>,
) {
  const effectiveColor = roi.color ?? parentColor;

  // if roi has bv_type, use its own segmentationId; otherwise inherit from parent
  const effectiveSegId =
    (roi.bv_type && setTypeToSegId?.get(roi.bv_type)) ?? segmentationId;

  if (roi.label != null && roi.code) {
    const codeParts = roi.code.split("-");

    segments.push({
      SegmentNumber: roi.label,
      SegmentLabel: roi.name ?? roi.code,
      SegmentedPropertyTypeCodeSequence: [
        {
          CodeValue: codeParts[0],
          ...(codeParts.length > 1
            ? {
                SegmentedPropertyTypeModifierCodeSequence: [
                  { CodeValue: codeParts[1] },
                ],
              }
            : {}),
        },
      ],
      RecommendedDisplayCIELabValue: undefined,
      _rgbColor: effectiveColor,
      selected: false,
      representationUID: effectiveSegId,
      seriesUID,
    } as any);
  }

  for (const child of roi.sub_rois ?? []) {
    collectRoiSegments(
      child,
      seriesUID,
      effectiveSegId,
      segments,
      effectiveColor,
      setTypeToSegId,
    );
  }
  for (const child of roi.lateral_rois ?? []) {
    collectRoiSegments(
      child,
      seriesUID,
      effectiveSegId,
      segments,
      effectiveColor,
      setTypeToSegId,
    );
  }
}

// ═══════════════════════════════════════════════════════════════
//  Original DICOM helpers — ALL UNCHANGED below
// ═══════════════════════════════════════════════════════════════

function generateRequests(customOrderedRequests, volumeId) {
  const requestType = "prefetch";
  const priority = 0;
  return customOrderedRequests.map(
    ({ callLoadImage, imageId, imageIdIndex, options }) => {
      const additionalDetails = { volumeId: volumeId };
      return {
        callLoadImage: callLoadImage.bind(this, imageId, imageIdIndex, options),
        requestType,
        additionalDetails,
        priority,
      };
    },
  );
}
function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

async function imageLoadPoolManagerRequestAsync(
  request,
  retries = 3,
  delay = 1000,
) {
  for (let attempt = 1; attempt <= retries; attempt++) {
    try {
      await new Promise<void>((resolve, reject) => {
        imageLoadPoolManager.addRequest(
          request.callLoadImage,
          request.requestType,
          request.additionalDetails,
          request.priority,
        );
        resolve();
      });
      return;
    } catch (error) {
      console.error(`Attempt ${attempt} failed:`, error);
      if (attempt < retries) {
        await sleep(delay);
      } else {
        console.error("All attempts failed.");
        throw error;
      }
    }
  }
}
async function loadFramesMiddleOutwards(
  volume,
  viewerIds: ViewerIds,
  renderingEngine: IRenderingEngine,
) {
  const { volumeId, viewportId1, viewportId2, viewportId3 } = viewerIds;
  const loadRequests = await volume.getImageLoadRequests();
  const totalFrames = loadRequests.length;
  const batchSize = 5;
  const middle = Math.floor(totalFrames / 2);
  const failedFrames = new Set<number>();
  const MAX_BATCH_RETRIES = 3;

  async function loadBatch(startIdx: number, endIdx: number, attempt = 0) {
    const batch = loadRequests.slice(startIdx, endIdx + 1);
    const requests = await generateRequests(batch, volumeId);

    try {
      await Promise.all(
        requests.map(async (request) => {
          try {
            await imageLoadPoolManagerRequestAsync(request);
          } catch (error) {
            failedFrames.add(startIdx + requests.indexOf(request));
            throw error;
          }
        }),
      );
    } catch (error) {
      if (attempt < MAX_BATCH_RETRIES) {
        console.warn(
          `Retrying batch ${startIdx}-${endIdx}, attempt ${attempt + 1}`,
        );
        await new Promise((resolve) =>
          setTimeout(resolve, 1000 * Math.pow(2, attempt)),
        );
        return loadBatch(startIdx, endIdx, attempt + 1);
      }
    }

    await Promise.all(
      [viewportId1, viewportId2, viewportId3].map(async (viewportId) => {
        const viewport = renderingEngine.getViewport(viewportId);
        viewport.render();
      }),
    );
  }

  for (
    let i = middle, j = middle + 1;
    i >= 0 || j < totalFrames;
    i -= batchSize, j += batchSize
  ) {
    const leftStart = Math.max(i - batchSize + 1, 0);
    const rightEnd = Math.min(j + batchSize - 1, totalFrames - 1);

    await Promise.all([loadBatch(leftStart, i), loadBatch(j, rightEnd)]);
  }
  if (failedFrames.size > 0) {
    console.warn(`Retrying ${failedFrames.size} failed frames individually`);
    await Promise.all(
      Array.from(failedFrames).map(async (frameIndex) => {
        const request = (
          await generateRequests([loadRequests[frameIndex]], volumeId)
        )[0];
        return imageLoadPoolManagerRequestAsync(request, 5, 2000);
      }),
    );
  }
}

async function createAndLoadVolume(
  viewerIds: ViewerIds,
  imageIds: string[],
  renderingEngine: IRenderingEngine,
) {
  const { volumeId, viewportId1, viewportId2, viewportId3 } = viewerIds;

  let volume = cache.getVolume(volumeId) as ImageVolume | undefined;
  if (!volume) {
    volume = await volumeLoader.createAndCacheVolume(volumeId, {
      imageIds,
    });
  }

  await setVolumesForViewports(
    renderingEngine,
    [
      {
        volumeId,
      },
    ],
    [viewportId1, viewportId2, viewportId3],
  );
  for (const vpId of [viewportId1, viewportId2, viewportId3]) {
    const vp = renderingEngine.getViewport(vpId) as any;
    vp.render();
  }
  return volume;
}

async function setupRenderingEngine(
  viewerRefs: RefObject<HTMLDivElement>[],
  id: string,
  { renderingEngineId, viewportInputConf }: RenderingEngineIds,
) {
  let renderingEngine = getRenderingEngine(renderingEngineId);
  if (!renderingEngine) {
    await initCornerstone();
    renderingEngine = new RenderingEngine(renderingEngineId);
  }
  const viewportInputs: PublicViewportInput[] = viewportInputConf.map(
    (v, i) => ({
      ...v,
      element: viewerRefs[i].current!,
    }),
  );
  renderingEngine.setViewports(viewportInputs);

  setupTools(id);
  return renderingEngine;
}

function setupTools(id: string) {
  const {
    renderingEngineId,
    toolGroupId,
    voiSynchronizerId,
    viewportId1,
    viewportId2,
    viewportId3,
    getReferenceLineColor,
    getReferenceLineControllable,
    getReferenceLineDraggableRotatable,
    getReferenceLineSlabThicknessControlsOn,
  } = getIds(id);

  const toolGroup =
    ToolGroupManager.getToolGroup(toolGroupId) ||
    ToolGroupManager.createToolGroup(toolGroupId);

  toolGroup.addViewport(viewportId1, renderingEngineId);
  toolGroup.addViewport(viewportId2, renderingEngineId);
  toolGroup.addViewport(viewportId3, renderingEngineId);

  if (!toolGroup._toolInstances[StackScrollTool.toolName]) {
    toolGroup.addTool(StackScrollTool.toolName);
    toolGroup.setToolActive(StackScrollTool.toolName, {
      bindings: [{ mouseButton: MouseBindings.Wheel }],
    });
  }

  if (!toolGroup._toolInstances[WindowLevelTool.toolName]) {
    toolGroup.addTool(WindowLevelTool.toolName);
  }

  if (!toolGroup._toolInstances[PanTool.toolName]) {
    toolGroup.addTool(PanTool.toolName);
  }

  if (!toolGroup._toolInstances[ZoomTool.toolName]) {
    toolGroup.addTool(ZoomTool.toolName);
  }

  if (!toolGroup._toolInstances[BrushTool.toolName]) {
    toolGroup.addTool(BrushTool.toolName);
    toolGroup.addToolInstance(
      brushInstanceNames.SphereBrush,
      BrushTool.toolName,
      {
        activeStrategy: brushStrategies[brushInstanceNames.SphereBrush],
      },
    );
    toolGroup.addToolInstance(
      brushInstanceNames.SphereEraser,
      BrushTool.toolName,
      {
        activeStrategy: brushStrategies[brushInstanceNames.SphereEraser],
      },
    );
  }

  const voiSynchronizer =
    cornerstoneTools.SynchronizerManager.getSynchronizer(voiSynchronizerId) ||
    cornerstoneTools.synchronizers.createVOISynchronizer(voiSynchronizerId, {
      syncInvertState: true,
      syncColormap: true,
    });
  voiSynchronizer.add({ renderingEngineId, viewportId: viewportId1 });
  voiSynchronizer.add({ renderingEngineId, viewportId: viewportId2 });
  voiSynchronizer.add({ renderingEngineId, viewportId: viewportId3 });

  if (!toolGroup._toolInstances[CrosshairsTool.toolName]) {
    toolGroup.addTool(CrosshairsTool.toolName, {
      getReferenceLineColor,
      getReferenceLineControllable,
      getReferenceLineDraggableRotatable,
      getReferenceLineSlabThicknessControlsOn,
    });
  }

  toolGroup.setToolActive(CrosshairsTool.toolName);

  toolGroup.setToolActive(WindowLevelTool.toolName, {
    bindings: [{ mouseButton: MouseBindings.Secondary }],
  });

  toolGroup.setToolActive(PanTool.toolName, {
    bindings: [{ mouseButton: MouseBindings.Auxiliary }],
  });

  toolGroup.setToolActive(ZoomTool.toolName, {
    bindings: [
      {
        mouseButton: MouseBindings.Auxiliary,
        modifierKey: KeyboardBindings.Ctrl,
      },
    ],
  });

  toolGroup.setToolActive(StackScrollTool.toolName);
  const { SegmentationRepresentations } = csToolsEnums;

  cstSegmentation.config.style.setStyle(
    {
      type: SegmentationRepresentations.Labelmap,
    },
    {
      renderOutline: false,
      outlineWidth: 0,
      outlineWidthInactive: 0,
      renderOutlineInactive: false,
      fillAlpha: 1,
      fillAlphaInactive: 1,
    },
  );
  utilities.segmentation.setBrushSizeForToolGroup(toolGroupId, 5);
}

async function setupSegmentation(
  { volumeId, segVolumeId, segmentationId, toolGroupId }: SegmentationIds,
  segInfoPromise: Promise<SegInfo>,
  notify: (message: string, options?: object) => void,
) {
  const toolGroup = ToolGroupManager.getToolGroup(toolGroupId);
  if (!toolGroup) return;

  const viewportIds = toolGroup.viewportsInfo.map((v) => v.viewportId);

  const anyViewportHasSeg = viewportIds.some((vpId) => {
    const reps =
      cstSegmentation.state.getSegmentationRepresentations(vpId) || [];
    return reps.some((rep: any) =>
      String(rep.segmentationId).includes(segmentationId),
    );
  });

  if (anyViewportHasSeg || !segInfoPromise) return;

  const segInfo = await segInfoPromise;
  const labelMapBuffers = segInfo.labelmapBufferArray;

  if (!labelMapBuffers?.length) {
    console.error("SEG parsing didn't return any buffer");
    notify("notification.error.segmentation.parsing", { type: "warning" });
    return;
  }

  await Promise.all(
    labelMapBuffers.map(async (_b, i) => {
      const derivedId = `${segVolumeId}_${i}`;
      if (cache.getVolume(derivedId)) return;

      const segVolume = await volumeLoader.createAndCacheDerivedVolume(
        volumeId,
        {
          volumeId: derivedId,
          targetBuffer: { type: "Uint8Array" },
        },
      );
      await new Promise((resolve) => setTimeout(resolve, 0));

      const cachedSegVolume = cache.getVolume(derivedId) as ImageVolume;

      if (cachedSegVolume) {
        injectBufferIntoSegVolume(cachedSegVolume, labelMapBuffers[i]);
      } else {
        console.error("Failed to get cached derived volume:", derivedId);
      }
    }),
  );

  const segmentations = labelMapBuffers
    .filter(
      (_b, i) =>
        !cstSegmentation.state.getSegmentation(`${segmentationId}_${i}`),
    )
    .map((_b, i) => ({
      segmentationId: `${segmentationId}_${i}`,
      representation: {
        type: csToolsEnums.SegmentationRepresentations.Labelmap,
        data: { volumeId: `${segVolumeId}_${i}` },
      },
    }));

  if (segmentations.length) {
    cstSegmentation.addSegmentations(segmentations);
  }

  const segmentationIds = labelMapBuffers.map(
    (_b, i) => `${segmentationId}_${i}`,
  );
  if (viewerState.instanceMetadata) {
    viewerState.segmentsPerSeries = viewerState.segmentsPerSeries.map(
      (segment) => {
        if (
          segment.seriesUID !== viewerState.instanceMetadata?.SeriesInstanceUID
        )
          return segment;

        const segmentIndex = segment.SegmentNumber;
        const bufferIndex = segInfo.segmentsOnFrameArray.findIndex((arr) =>
          arr.flat().includes(segmentIndex),
        );
        const representationUID =
          bufferIndex < 0 ? segmentationIds[0] : segmentationIds[bufferIndex];

        return { ...segment, representationUID, selected: false };
      },
    );
  }
}
